// eslint-disable-next-line no-restricted-imports
import Image, { ImageProps, StaticImageData } from "next/image";

type FluidImageProps = Omit<ImageProps, "src"> & {
  src: NonNullable<ImageProps["src"]>; // Explicitly require src (no null/undefined)
  surpressWarning?: boolean;
};

type StaticRequire = { default: StaticImageData };
type StaticImport = StaticRequire | StaticImageData;
type ImageSrc = string | StaticImport;

function isValidFluidUrl(url: string): boolean {
  return url.startsWith("https://assets.fluid.app/");
}

function isStaticImageData(src: StaticImport): src is StaticImageData {
  return typeof src === "object" && "src" in src;
}

function isStaticRequire(src: StaticImport): src is StaticRequire {
  return typeof src === "object" && "default" in src;
}

function getSrcString(src: ImageSrc): string {
  if (typeof src === "string") return src;
  if (isStaticImageData(src)) return src.src;
  if (isStaticRequire(src)) return src.default.src;
  return String(src);
}

function isSvg(src: ImageSrc): boolean {
  return getSrcString(src).endsWith(".svg");
}

export default function FluidImage(props: FluidImageProps) {
  const { src, surpressWarning, ...restProps } = props;

  // Handle undefined/null src
  if (!src) {
    console.warn("FluidImage: src prop is required");
    return null;
  }

  // Validate URL if it's a string
  if (typeof src === "string" && !isValidFluidUrl(src) && !surpressWarning) {
    console.warn(
      "Invalid image source. URLs must start with https://assets.fluid.app/",
    );
    if (process.env.NODE_ENV === "development") {
      return (
        <div>
          use the CDN or if the img is external use the img tag and eslint
          disable
        </div>
      );
    }
    return (
      // eslint-disable-next-line jsx-a11y/alt-text
      <img src={getSrcString(src)} {...restProps} />
    );
  }

  // Use Next/Image only for SVGs, otherwise use regular img tag
  if (isSvg(src)) {
    // eslint-disable-next-line jsx-a11y/alt-text -- alt passed via restProps
    return <Image src={src} {...restProps} />;
  } else {
    return (
      // eslint-disable-next-line jsx-a11y/alt-text
      <img src={getSrcString(src)} {...restProps} />
    );
  }
}
